import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, FormArray, FormBuilder } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { TextareaCounterComponent } from '../../../../shared/components/textarea-counter/textarea-counter.component';
import { WitnessValidatorService, Witness } from '../../../../services/validator/witness-validator.service';
import { YesNoCheckboxComponent } from '../../../../shared/components/yes-no-checkbox/yes-no-checkbox.component';
import { MaterialModule } from '../../../../shared/material.module';
import { InternationalPhoneInputComponent } from '../../../../shared/components/international-phone-input/international-phone-input.component';
import { PhoneValidatorService } from '../../../../services/validator/phone-validator.service';

@Component({
  selector: 'app-step-damage-description',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MaterialModule,
    TranslatePipe,
    TextareaCounterComponent,
    YesNoCheckboxComponent,
    InternationalPhoneInputComponent,
  ],
  templateUrl: './step-damage-description.component.html',
  styleUrl: './step-damage-description.component.scss',
})
export class StepDamageDescriptionComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() licensePlate = '';

  witnesses: Witness[] = [{ nome: '', cognome: '', email: '', telefono: '', dichiarazione: '' }];
  phoneValidationStates: boolean[] = [true];
  emailValidationStates: boolean[] = [true];

  constructor(
    private witnessValidator: WitnessValidatorService,
    private phoneValidator: PhoneValidatorService,
    private fb: FormBuilder,
  ) {}

  private syncWitnessesToForm(): void {
    const witnessesArray = this.form.get('witnesses') as FormArray;
    witnessesArray.clear();
    this.witnesses.forEach(witness => {
      witnessesArray.push(this.fb.group(witness));
    });
  }

  private loadWitnessesFromForm(): void {
    const witnessesArray = this.form.get('witnesses') as FormArray;
    if (witnessesArray.length > 0) {
      this.witnesses = witnessesArray.value;
    }
  }

  ngOnInit(): void {
    this.loadWitnessesFromForm();
    this.emailValidationStates = this.witnesses.map(() => true);
  }

  getPresenzaFeriti(): string | null {
    return this.form.get('presenzaFeriti')?.value || null;
  }

  setPresenzaFeriti(value: string): void {
    this.form.get('presenzaFeriti')?.setValue(value);
  }

  clearPresenzaFeriti(): void {
    this.form.get('presenzaFeriti')?.setValue('');
  }

  getDanniMateriali(): string | null {
    return this.form.get('danniMateriali')?.value || null;
  }

  setDanniMateriali(value: string): void {
    this.form.get('danniMateriali')?.setValue(value);
  }

  clearDanniMateriali(): void {
    this.form.get('danniMateriali')?.setValue('');
  }

  getPresenzaTestimoni(): string | null {
    return this.form.get('presenzaTestimoni')?.value || null;
  }

  setPresenzaTestimoni(value: string): void {
    this.form.get('presenzaTestimoni')?.setValue(value);
    if (value === 'false') {
      this.witnesses = [{ nome: '', cognome: '', email: '', telefono: '', dichiarazione: '' }];
      this.phoneValidationStates = [true];
      this.emailValidationStates = [true];
    }
  }

  clearPresenzaTestimoni(): void {
    this.form.get('presenzaTestimoni')?.setValue('');
    this.witnesses = [{ nome: '', cognome: '', email: '', telefono: '', dichiarazione: '' }];
    this.phoneValidationStates = [true];
    this.emailValidationStates = [true];
  }

  isWitnessValid(witness: Witness): boolean {
    return this.witnessValidator.isWitnessValid(witness);
  }

  isWitnessEmpty(witness: Witness): boolean {
    return this.witnessValidator.isWitnessEmpty(witness);
  }

  addWitness(): void {
    this.witnesses.push({ nome: '', cognome: '', email: '', telefono: '', dichiarazione: '' });
    this.phoneValidationStates.push(true);
    this.emailValidationStates.push(true);
    this.syncWitnessesToForm();
    this.form.updateValueAndValidity();
  }

  removeWitness(index: number): void {
    this.witnesses.splice(index, 1);
    this.phoneValidationStates.splice(index, 1);
    this.emailValidationStates.splice(index, 1);
    if (this.witnesses.length === 0) {
      this.witnesses.push({ nome: '', cognome: '', email: '', telefono: '', dichiarazione: '' });
      this.phoneValidationStates.push(true);
      this.emailValidationStates.push(true);
    }
    this.syncWitnessesToForm();
    this.form.updateValueAndValidity();
  }

  triggerValidation(): void {
    this.syncWitnessesToForm();
    this.form.updateValueAndValidity();
    setTimeout(() => this.form.updateValueAndValidity(), 0);
    if ((window as any).wizardComponent) {
      (window as any).wizardComponent.cdr.detectChanges();
    }
  }

  get isValid(): boolean | undefined {
    const descriptionField = this.form.get('descrizioneDanni');
    const hasValidDescription = descriptionField?.valid && descriptionField?.value?.trim().length >= 10;
    const hasInjuriesAnswer = this.form.get('presenzaFeriti')?.valid;
    const hasMaterialDamageAnswer = this.form.get('danniMateriali')?.valid;
    const hasWitnessesAnswer = this.form.get('presenzaTestimoni')?.valid;
    const witnessesValid = this.validateWitnesses();
    return !!(hasValidDescription && hasInjuriesAnswer && hasMaterialDamageAnswer && hasWitnessesAnswer && witnessesValid);
  }

  onPhoneValidation(witnessIndex: number, isValid: boolean): void {
    this.phoneValidationStates[witnessIndex] = isValid;
    this.triggerValidation();
  }

  isPhoneValid(witnessIndex: number): boolean {
    const witness = this.witnesses[witnessIndex];
    if (!witness.telefono) return true;
    return this.phoneValidationStates[witnessIndex] || false;
  }

  areAllPhonesValid(): boolean {
    return this.witnesses.every((witness, index) => {
      if (!witness.telefono) return true;
      return this.phoneValidationStates[index];
    });
  }

  onEmailBlur(witnessIndex: number): void {
    this.validateEmail(witnessIndex);
    this.triggerValidation();
  }

  validateEmail(witnessIndex: number): void {
    const witness = this.witnesses[witnessIndex];
    this.emailValidationStates[witnessIndex] = this.witnessValidator.isEmailValid(witness.email);
  }

  isEmailValid(witnessIndex: number): boolean {
    const witness = this.witnesses[witnessIndex];
    if (!witness.email) return true;
    return this.emailValidationStates[witnessIndex] || false;
  }

  areAllEmailsValid(): boolean {
    return this.witnesses.every((witness, index) => {
      if (!witness.email) return true;
      return this.emailValidationStates[index];
    });
  }

  validateWitnesses(): boolean {
    const presenzaTestimoni = this.getPresenzaTestimoni();
    if (presenzaTestimoni !== 'true') return true;
    const allWitnessesValid = this.witnesses.every(w => this.witnessValidator.isWitnessValid(w));
    const allPhonesValid = this.areAllPhonesValid();
    const allEmailsValid = this.areAllEmailsValid();
    return allWitnessesValid && allPhonesValid && allEmailsValid;
  }
}
